// src/hooks/context.tsx
import {
  createTranslator,
  LiveChatClient
} from "@kobecuppens/livechat-core";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { jsx } from "react/jsx-runtime";
var LiveChatContext = createContext(null);
function LiveChatProvider({ client: provided, strings, children, ...options }) {
  const [client] = useState(() => provided ?? new LiveChatClient(options));
  const state = useSyncExternalStore(client.store.subscribe, client.store.getSnapshot, client.store.getSnapshot);
  useEffect(() => {
    client.init().catch(() => {
    });
    return () => {
      if (!provided) client.destroy();
    };
  }, [client, provided]);
  const userKey = options.user ? `${options.user.id}:${options.user.hash}` : "";
  const userRef = useRef(options.user);
  userRef.current = options.user;
  const firstUser = useRef(true);
  useEffect(() => {
    if (firstUser.current) {
      firstUser.current = false;
      return;
    }
    client.identify(userRef.current ?? null).catch((err) => {
      client.store.set({ error: err instanceof Error ? err.message : String(err) });
    });
  }, [client, userKey]);
  useEffect(() => {
    if (options.locale) client.setLocale(options.locale);
  }, [client, options.locale]);
  const [stack, setStack] = useState([{ name: "home" }]);
  const [isOpen, setOpen] = useState(false);
  const messenger = useMemo(
    () => ({
      isOpen,
      route: stack[stack.length - 1],
      canGoBack: stack.length > 1,
      open: (route) => {
        if (route) setStack(route.name === "home" ? [route] : [{ name: "home" }, route]);
        setOpen(true);
      },
      close: () => setOpen(false),
      toggle: () => setOpen((o) => !o),
      navigate: (route) => setStack((s) => [...s, route]),
      replace: (route) => setStack((s) => [...s.slice(0, -1), route]),
      back: () => setStack((s) => s.length > 1 ? s.slice(0, -1) : s)
    }),
    [isOpen, stack]
  );
  const t = useMemo(() => createTranslator(state.locale, strings), [state.locale, strings]);
  const value = useMemo(() => ({ client, t, messenger }), [client, t, messenger]);
  return /* @__PURE__ */ jsx(LiveChatContext.Provider, { value, children });
}
function useCtx() {
  const ctx = useContext(LiveChatContext);
  if (!ctx) throw new Error("[livechat] Wrap your app in <LiveChatProvider>.");
  return ctx;
}
function useLiveChatClient() {
  return useCtx().client;
}
function useLiveChatState(selector = (s) => s) {
  const client = useCtx().client;
  const get = useCallback(() => selector(client.store.getSnapshot()), [client, selector]);
  return useSyncExternalStore(client.store.subscribe, get, get);
}
function useTranslate() {
  return useCtx().t;
}
function useMessenger() {
  return useCtx().messenger;
}
var selectUnread = (s) => s.unreadCount;
function useUnreadCount() {
  return useLiveChatState(selectUnread);
}

// src/hooks/data.ts
import {
  DRAFT_CONVERSATION,
  EMPTY_THREAD
} from "@kobecuppens/livechat-core";
import { useCallback as useCallback2, useEffect as useEffect2, useState as useState2 } from "react";
function useConversation(conversationId, onCreated) {
  const client = useLiveChatClient();
  const ready = useLiveChatState((s) => s.status === "ready");
  const threadId = conversationId ?? DRAFT_CONVERSATION;
  const thread = useLiveChatState(useCallback2((s) => s.threads[threadId] ?? EMPTY_THREAD, [threadId]));
  const conversation = useLiveChatState(
    useCallback2((s) => conversationId ? s.conversations.find((c) => c.id === conversationId) ?? null : null, [conversationId])
  );
  const identity = useLiveChatState((s) => s.identity);
  useEffect2(() => {
    if (!conversationId || !ready) return;
    return client.openConversation(conversationId);
  }, [client, conversationId, ready, identity]);
  const send = useCallback2(
    async (body, attachmentIds, attachments) => {
      const id = await client.sendMessage(conversationId, { body, attachmentIds, attachments });
      if (!conversationId && id) onCreated?.(id);
      return id;
    },
    [client, conversationId, onCreated]
  );
  return {
    thread,
    conversation,
    send,
    retry: useCallback2((clientId) => client.retry(clientId), [client]),
    discard: useCallback2((clientId) => client.discard(clientId), [client]),
    loadOlder: useCallback2(() => conversationId ? client.loadOlder(conversationId) : Promise.resolve(), [client, conversationId]),
    setTyping: useCallback2((typing) => conversationId && client.setTyping(conversationId, typing), [client, conversationId]),
    submitCsat: useCallback2(
      (score, comment) => conversationId ? client.submitCsat(conversationId, { score, comment }) : Promise.resolve(),
      [client, conversationId]
    )
  };
}
function useConversations() {
  const client = useLiveChatClient();
  const ready = useLiveChatState((s) => s.status === "ready");
  const conversations = useLiveChatState((s) => s.conversations);
  const loaded = useLiveChatState((s) => s.conversationsLoaded);
  const [error, setError] = useState2(null);
  const refresh = useCallback2(() => {
    setError(null);
    return client.refreshConversations().catch((e) => setError(e.message));
  }, [client]);
  const identity = useLiveChatState((s) => s.identity);
  useEffect2(() => {
    if (ready) void refresh();
  }, [ready, refresh, identity]);
  return { conversations, loaded, error, refresh };
}
function useAsync(load, deps) {
  const [state, setState] = useState2({ data: null, loading: !!load, error: null });
  const [nonce, setNonce] = useState2(0);
  useEffect2(() => {
    if (!load) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    load().then(
      (data) => !cancelled && setState({ data, loading: false, error: null }),
      (e) => !cancelled && setState({ data: null, loading: false, error: e.message })
    );
    return () => {
      cancelled = true;
    };
  }, [...deps, nonce]);
  return { ...state, reload: () => setNonce((n) => n + 1) };
}
function useHelpHome() {
  const client = useLiveChatClient();
  const locale = useLiveChatState((s) => s.locale);
  const ready = useLiveChatState((s) => s.status === "ready");
  return useAsync(
    ready ? async () => {
      const [categories, popular] = await Promise.all([
        client.http.listFaqCategories(locale),
        client.http.listFaqArticles({ locale, sort: "popular", limit: 5 })
      ]);
      return { categories, popular };
    } : null,
    [client, locale, ready]
  );
}
function useCategoryArticles(categoryId) {
  const client = useLiveChatClient();
  const locale = useLiveChatState((s) => s.locale);
  return useAsync(() => client.http.listFaqArticles({ locale, category: categoryId }), [client, locale, categoryId]);
}
function useArticle(slug) {
  const client = useLiveChatClient();
  const locale = useLiveChatState((s) => s.locale);
  const result = useAsync(() => client.http.getFaqArticle(slug, locale), [client, slug, locale]);
  const [feedback, setFeedback] = useState2(null);
  const sendFeedback = useCallback2(
    (helpful) => {
      if (!result.data || feedback !== null) return;
      setFeedback(helpful);
      void client.http.sendFaqFeedback(result.data.id, helpful).catch(() => {
      });
    },
    [client, result.data, feedback]
  );
  return { ...result, feedback, sendFeedback };
}
function useDebounced(value, ms) {
  const [debounced, setDebounced] = useState2(value);
  useEffect2(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
function useHelpSearch(query, { debounceMs = 200, mode = "all", limit = 8 } = {}) {
  const client = useLiveChatClient();
  const locale = useLiveChatState((s) => s.locale);
  const q = useDebounced(query.trim(), debounceMs);
  const minLength = mode === "any" ? 8 : 2;
  const result = useAsync(
    q.length >= minLength ? () => client.http.searchFaq(q, { locale, mode, limit }) : null,
    [client, q, locale, mode, limit]
  );
  return { ...result, query: q, pending: query.trim() !== q || result.loading };
}
function useArticleSuggestions(draft) {
  const { data } = useHelpSearch(draft, { debounceMs: 500, mode: "any", limit: 3 });
  return data ?? [];
}
function useWorkspaceConfig() {
  return useLiveChatState((s) => s.config);
}

export {
  LiveChatProvider,
  useLiveChatClient,
  useLiveChatState,
  useTranslate,
  useMessenger,
  useUnreadCount,
  useConversation,
  useConversations,
  useHelpHome,
  useCategoryArticles,
  useArticle,
  useHelpSearch,
  useArticleSuggestions,
  useWorkspaceConfig
};
